import {
  TabItem,
  Tabs,
  useModals,
  Modal,
  ModalProps,
  Button,
  Input,
  Select,
  Stack,
  Title,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import { useWorkflowUpdateBlockActionGoogleWorkspace } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiWorkflowBlockActionGoogleWorkspace,
  ApiWorkflowBlockActionGoogleWorkspaceSave,
  ApiWorkflowBlockActionGoogleWorkspaceType,
} from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiWorkflowBlockActionTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { WorkflowActionTimingEditor } from "./WorkflowActionTimingEditor.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
  useWorkflows,
  WorkflowActionTimingFormFields,
} from "@/pages/workflows/workflow-hook.tsx";

export interface WorkflowEditActionGoogleWorkspaceModalProps extends ModalProps {
  workflowVersion: ApiWorkflowVersion;
  block: ApiWorkflowBlock;
  actionGoogleWorkspace: ApiWorkflowBlockActionGoogleWorkspace;
}

interface FormData extends WorkflowActionTimingFormFields {
  title: string | null;
  action_type: ApiWorkflowBlockActionGoogleWorkspaceType;
}

export const WorkflowEditActionGoogleWorkspaceModal = ({
  workflowVersion,
  block,
  actionGoogleWorkspace,
  ...props
}: WorkflowEditActionGoogleWorkspaceModalProps) => {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { workflowActionTimingSchemaFields } = useWorkflows();
  const schemaObject = yup.object({
    title: yup.string().defined().nullable(),
    ...workflowActionTimingSchemaFields,
    action_type: yup
      .mixed<ApiWorkflowBlockActionGoogleWorkspaceType>()
      .oneOf(Object.values(ApiWorkflowBlockActionGoogleWorkspaceType))
      .required(t("errors:field_required")),
  });
  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormData>({
    mode: "onSubmit",
    resolver: yupResolver(schemaObject),
    values: {
      title: block.title ?? "",
      ...getWorkflowActionTimingValues(block),
      action_type: actionGoogleWorkspace.action_type,
    },
  });

  const updateBlockAction = useWorkflowUpdateBlockActionGoogleWorkspace(control);

  const handleSave = handleSubmit(async (data) => {
    try {
      const blockData: ApiWorkflowBlockActionGoogleWorkspaceSave = {
        action_type: data.action_type,
      };

      await updateBlockAction.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
        payload: {
          title: data.title,
          ...getWorkflowActionTimingPayload(data),
          block_data: blockData,
          conditions: conditions.map((x) => ({ ...x })),
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.blockEdited, {
        [analyticProperties.id]: workflowVersion.workflow_id,
        [analyticProperties.actionType]: ApiWorkflowBlockActionTypes.google_workspace,
      });

      close(id);
    } catch {
      // handle error
    }
  });

  const handleTabChanged = (tabIndex: number) => {
    setActiveTab(tabIndex);
  };

  const [activeTab, setActiveTab] = useState(0);

  return (
    <>
      <Modal
        {...props}
        variant={"side"}
        title={t("workflow:action_type_google_workspace")}
        layout={"stretch"}
        withCloser
        closeByEscEnabled
        size={"lg"}
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              size={"lg"}
              className={"w-full"}
              type={"submit"}
              onClick={handleSave}
              isLoading={updateBlockAction.isPending}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={handleSave}>
          <Tabs onActiveTabChange={handleTabChanged} activeTab={activeTab}>
            <TabItem title={t("common:settings")}>
              <Stack gap={"lg"} className={"py-6"}>
                <Stack>
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <Select
                        label={t("workflow:action_type_google_workspace_action")}
                        emptyTitle={t("common:select")}
                        value={value}
                        required={true}
                        options={[
                          {
                            id: ApiWorkflowBlockActionGoogleWorkspaceType.create_user,
                            title: t("workflow:action_type_google_workspace_create_user"),
                          },
                          {
                            id: ApiWorkflowBlockActionGoogleWorkspaceType.delete_user,
                            title: t("workflow:action_type_google_workspace_delete_user"),
                          },
                        ]}
                        onChange={onChange}
                        error={errors.action_type?.message}
                      />
                    )}
                    control={control}
                    name={"action_type"}
                  />
                </Stack>
              </Stack>
            </TabItem>
            <TabItem title={t("workflow:conditions")}>
              <Stack gap={"lg"} className={"py-6"}>
                <Stack>
                  <Title header={t("workflow:block")} size={"xs"} />
                  <Input
                    label={t("common:title")}
                    type="text"
                    {...register("title")}
                    error={errors.title?.message}
                  />
                </Stack>
                <WorkflowActionTimingEditor
                  control={control}
                  register={register}
                  errors={errors}
                  triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
                />

                <WorkflowBlockConditionsList
                  conditions={conditions}
                  onAdd={handleAddCondition}
                  onEdit={handleEditCondition}
                  onDelete={handleDeleteCondition}
                />
              </Stack>
            </TabItem>
          </Tabs>
        </form>
      </Modal>
    </>
  );
};
